<script setup lang="ts">
// Adding files to collections (HRTH-164.7): a file can be in any number of them, so each
// collection is a check box, on when all the files are in it; a new one starts with them.
import { ref } from 'vue'
import { describeError } from '../api/http'
import type { Collection } from '../lib/collections'
import type { OpenEntry } from '../lib/libraryPlay'
import Icon from './Icon.vue'
import Modal from './Modal.vue'

const props = defineProps<{
  files: OpenEntry[]
  collections: Collection<OpenEntry>[]
  toggle: (c: Collection<OpenEntry>, on: boolean) => Promise<unknown>
  create: (name: string) => Promise<unknown>
}>()
const emit = defineEmits<{ close: [] }>()

// By id: the library reloads after each change, with new objects for the same files.
const has = (c: Collection<OpenEntry>) => props.files.every((f) => c.items.some((i) => i.entry.id === f.entry.id))
const name = ref('')
const busy = ref<string | null>(null)
const error = ref<string | null>(null)

async function run(id: string, fn: () => Promise<unknown>, close = false): Promise<void> {
  busy.value = id
  error.value = null
  try {
    await fn()
    if (close) emit('close')
  } catch (e) {
    error.value = describeError(e)
  } finally {
    busy.value = null
  }
}
</script>

<template>
  <Modal :title="files.length === 1 ? 'Add to collection' : `Add ${files.length} files to`" @close="emit('close')">
    <div class="space-y-4" data-testid="collection-add">
      <div v-if="collections.length" class="-mx-5 max-h-72 overflow-y-auto border-y border-border">
        <button
          v-for="c in collections"
          :key="c.id"
          class="flex min-h-12 w-full items-center gap-3 border-b border-border px-5 text-left last:border-b-0 hover:bg-bg-hover disabled:opacity-50"
          :disabled="!!busy"
          role="checkbox"
          :aria-checked="has(c)"
          :data-testid="`add-to-${c.name}`"
          @click="run(c.id, () => toggle(c, !has(c)))"
        >
          <span class="flex h-5 w-5 shrink-0 items-center justify-center border border-ink" :class="has(c) ? 'bg-ink text-paper' : ''">
            <Icon v-if="has(c)" name="check" />
          </span>
          <span class="min-w-0 flex-1">
            <span class="block truncate text-sm">{{ c.name }}</span>
            <span class="mono block text-text-muted">{{ c.items.length }} file{{ c.items.length === 1 ? '' : 's' }}</span>
          </span>
        </button>
      </div>
      <form class="flex gap-2" @submit.prevent="name.trim() && run('new', () => create(name.trim()), true)">
        <input v-model="name" class="input min-w-0 flex-1" maxlength="200" placeholder="New collection" aria-label="New collection" data-testid="new-collection-name" />
        <button class="btn-primary" :disabled="!!busy || !name.trim()" data-testid="new-collection-save">Create</button>
      </form>
      <p v-if="error" class="text-sm text-error">{{ error }}</p>
      <div class="flex justify-end">
        <button class="btn-ghost" data-testid="collection-add-done" @click="emit('close')">Done</button>
      </div>
    </div>
  </Modal>
</template>
